import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  CircleAlert,
  DollarSign,
  FileText,
  Film,
  Image as ImageIcon,
  Music2,
  RefreshCw,
} from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { getPublicVideoLabel } from "../../utils/projectRuntime";

// 模型中文名映射
const MODEL_NAMES = {
  // 图片模型
  gemini: "Gemini",
  wangsuGemini: "Gemini (网宿)",
  omini: "Omini",
  omini_openai: "Omini OpenAI",
  wanx: "万相",
  wuyin: "无影",
  doubao: "豆包",
  deepseek: "DeepSeek",
  hailuo: "海螺",
  keling: "可灵",
  // 视频模型
  kling: "可灵",
  "kling-v3": "可灵 V3",
  "kling-v3-omni": "可灵 V3 Omni",
  "kling-video-o1": "可灵 Video O1",
  "kling-v2-6": "可灵 V2.6",
  vidu: "Vidu",
  "viduq3-turbo": "Vidu Q3 Turbo",
  "viduq3-pro": "Vidu Q3 Pro",
  "viduq2-pro-fast": "Vidu Q2 Pro Fast",
  "viduq2-pro": "Vidu Q2 Pro",
  "viduq2-turbo": "Vidu Q2 Turbo",
  "viduq1": "Vidu Q1",
  "vidu2.0": "Vidu 2.0",
  minimax: "海螺视频",
  "MiniMax-Hailuo-2.3": "海螺 2.3",
  wangsuSora: "Sora 2",
  "sora-2": "Sora 2",
  "sora-2-pro": "Sora 2 Pro",
  jimeng: "即梦",
  wanxVideo: "万相视频",
};

const getModelName = (id) => MODEL_NAMES[id] || getPublicVideoLabel(id, id);

// 时间范围选项
const TIME_RANGES = [
  { id: "all", label: "全部" },
  { id: "7d", label: "近7天" },
  { id: "30d", label: "近30天" },
  { id: "90d", label: "近90天" },
];

const getTimeRange = (rangeId) => {
  const now = Date.now();
  switch (rangeId) {
    case "7d": return { startTime: now - 7 * 86400000, endTime: now };
    case "30d": return { startTime: now - 30 * 86400000, endTime: now };
    case "90d": return { startTime: now - 90 * 86400000, endTime: now };
    default: return { startTime: null, endTime: null };
  }
};

const CostStatisticsPage = () => {
  const { projectId } = useParams();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("all");

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { startTime, endTime } = getTimeRange(timeRange);
      let url = `${API_HOST}/api/project-cost/report?projectId=${projectId}`;
      if (startTime) url += `&startTime=${startTime}`;
      if (endTime) url += `&endTime=${endTime}`;

      const data = await authenticatedFetch(url);
      setReport(data);
    } catch (e) {
      toast.error("获取费用报告失败: " + e.message, 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) fetchReport();
  }, [projectId, timeRange]);

  const formatCost = (cost) => {
    if (cost == null || cost === 0) return "¥0.00";
    const value = Number(cost);
    if (!Number.isFinite(value)) return "¥0.00";
    const digits = Math.abs(value) < 0.01 ? 6 : Math.abs(value) < 1 ? 4 : 2;
    return `¥${value.toFixed(digits).replace(/0+$/, "").replace(/\.$/, "")}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    if (seconds < 60) return `${seconds}s`;
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return sec > 0 ? `${min}m${sec}s` : `${min}m`;
  };

  const formatTokens = (tokens) => {
    const value = Number(tokens || 0);
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return `${value}`;
  };

  const isEmptyReport = report && [
    report.imageDetails,
    report.videoDetails,
    report.textDetails,
    report.audioDetails,
    report.otherDetails,
  ].every((items) => !items || items.length === 0);

  return (
    <div className="w-full h-full flex flex-col font-sans" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      {/* 顶部工具栏 */}
      <div
        className="h-14 border-b flex items-center justify-between px-6 shrink-0"
        style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" }}
      >
        <div className="flex items-center gap-4">
          <Link
            to={`/project/${projectId}`}
            className="p-1.5 rounded-lg transition-all duration-200"
            style={{ color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}
            title="返回项目"
          >
            <ArrowLeft size={16} />
          </Link>
          <div className="h-4 w-px" style={{ backgroundColor: "var(--color-border-primary)" }} />
          <h1 className="font-bold text-lg flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
            <DollarSign style={{ color: theme.colors.bluePrimary }} size={16} /> 费用统计
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {/* 时间范围选择 */}
          <div className="flex items-center gap-1 p-0.5 rounded-lg" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
            {TIME_RANGES.map((r) => (
              <button
                key={r.id}
                onClick={() => setTimeRange(r.id)}
                className="px-3 py-1 rounded-md text-xs font-medium transition-all"
                style={{
                  backgroundColor: timeRange === r.id ? theme.colors.bluePrimary : "transparent",
                  color: timeRange === r.id ? "white" : "var(--color-text-muted)",
                }}
              >
                {r.label}
              </button>
            ))}
          </div>

          <button
            onClick={fetchReport}
            disabled={loading}
            className="p-2 rounded-lg transition-all duration-200"
            style={{ color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}
            title="刷新"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <div className="max-w-5xl mx-auto space-y-6">
          {loading && !report ? (
            <div className="flex items-center justify-center py-32">
              <RefreshCw size={24} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
            </div>
          ) : report ? (
            <>
              {/* 汇总卡片 */}
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <SummaryCard
                  icon={<DollarSign size={18} />}
                  label="总费用"
                  value={formatCost(report.totalCost)}
                  color={theme.colors.bluePrimary}
                  highlight
                />
                <SummaryCard
                  icon={<ImageIcon size={18} />}
                  label="图片费用"
                  value={formatCost(report.totalImageCost)}
                  sub={`${report.totalImageCount || 0} 张`}
                  color="var(--color-purple-primary)"
                />
                <SummaryCard
                  icon={<Film size={18} />}
                  label="视频费用"
                  value={formatCost(report.totalVideoCost)}
                  sub={`${report.totalVideoCount || 0} 个 / ${formatDuration(report.totalVideoDuration || 0)}`}
                  color="var(--color-warning)"
                />
                <SummaryCard
                  icon={<FileText size={18} />}
                  label="文字费用"
                  value={formatCost(report.totalTextCost)}
                  sub={`${report.totalTextCount || 0} 次`}
                  color="var(--color-info)"
                />
                <SummaryCard
                  icon={<Music2 size={18} />}
                  label="音频费用"
                  value={formatCost(report.totalAudioCost)}
                  sub={`${report.totalAudioCount || 0} 次`}
                  color="var(--color-pink-primary)"
                />
                <SummaryCard
                  icon={<Activity size={18} />}
                  label="总生成次数"
                  value={`${report.totalGenerationCount ?? (
                    (report.totalImageCount || 0)
                    + (report.totalVideoCount || 0)
                    + (report.totalTextCount || 0)
                    + (report.totalAudioCount || 0)
                    + (report.totalOtherCount || 0)
                  )}`}
                  sub="可计费成功任务"
                  color="var(--color-success)"
                />
              </div>

              {(report.estimatedCount > 0 || report.zeroCostCount > 0) && (
                <div
                  className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 rounded-lg text-xs"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, var(--color-bg-card))",
                    border: "1px solid color-mix(in srgb, var(--color-warning) 35%, var(--color-border-secondary))",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  <CircleAlert size={15} style={{ color: "var(--color-warning)" }} />
                  {report.estimatedCount > 0 && (
                    <span>{report.estimatedCount} 次调用使用本地用量估算</span>
                  )}
                  {report.zeroCostCount > 0 && (
                    <span>{report.zeroCostCount} 次成功调用当前结算金额为 0，补充单价后可统一重算</span>
                  )}
                </div>
              )}

              {/* 图片明细 */}
              {report.imageDetails && report.imageDetails.length > 0 && (
                <DetailSection
                  title="图片生成明细"
                  icon={<ImageIcon size={14} />}
                  details={report.imageDetails}
                  formatCost={formatCost}
                  formatTokens={formatTokens}
                  unitLabel="张"
                  theme={theme}
                />
              )}

              {/* 视频明细 */}
              {report.videoDetails && report.videoDetails.length > 0 && (
                <DetailSection
                  title="视频生成明细"
                  icon={<Film size={14} />}
                  details={report.videoDetails}
                  formatCost={formatCost}
                  formatDuration={formatDuration}
                  formatTokens={formatTokens}
                  isVideo
                  unitLabel="次"
                  theme={theme}
                />
              )}

              {report.textDetails && report.textDetails.length > 0 && (
                <DetailSection
                  title="文字生成明细"
                  icon={<FileText size={14} />}
                  details={report.textDetails}
                  formatCost={formatCost}
                  formatTokens={formatTokens}
                  showTokens
                  unitLabel="次"
                  theme={theme}
                />
              )}

              {report.audioDetails && report.audioDetails.length > 0 && (
                <DetailSection
                  title="音乐与音频明细"
                  icon={<Music2 size={14} />}
                  details={report.audioDetails}
                  formatCost={formatCost}
                  formatTokens={formatTokens}
                  unitLabel="次"
                  theme={theme}
                />
              )}

              {report.otherDetails && report.otherDetails.length > 0 && (
                <DetailSection
                  title="其他生成明细"
                  icon={<Activity size={14} />}
                  details={report.otherDetails}
                  formatCost={formatCost}
                  formatTokens={formatTokens}
                  unitLabel="次"
                  theme={theme}
                />
              )}

              {/* 空状态 */}
              {isEmptyReport && (
                <div className="flex flex-col items-center justify-center py-20">
                  <DollarSign size={48} className="mb-4 opacity-20" style={{ color: "var(--color-text-muted)" }} />
                  <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
                    该时间范围内暂无生成记录
                  </p>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// 汇总卡片
const SummaryCard = ({ icon, label, value, sub }) => (
  <div
    className="p-4 rounded-xl transition-colors duration-150"
    style={{
      backgroundColor: "var(--color-bg-card)",
      border: "1px solid var(--color-border-secondary)",
    }}
  >
    <div className="flex items-center gap-2 mb-2">
      <span style={{ color: "var(--color-text-muted)" }}>{icon}</span>
      <span className="text-xs font-medium" style={{ color: "var(--color-text-muted)" }}>{label}</span>
    </div>
    <div className="text-xl font-bold" style={{ color: "var(--color-text-primary)" }}>{value}</div>
    {sub && <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>{sub}</div>}
  </div>
);

// 明细表格
const DetailSection = ({
  title,
  icon,
  details,
  formatCost,
  formatDuration,
  formatTokens,
  isVideo,
  showTokens,
  unitLabel = "次",
  theme,
}) => (
  <div
    className="rounded-xl overflow-hidden"
    style={{ backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border-secondary)" }}
  >
    <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
      <span style={{ color: theme.colors.bluePrimary }}>{icon}</span>
      <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{title}</span>
    </div>

    <div className="overflow-x-auto custom-scrollbar">
    <table className="w-full min-w-[720px] text-sm">
      <thead>
        <tr style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
          <th className="text-left px-5 py-2 font-medium" style={{ color: "var(--color-text-muted)" }}>模型</th>
          <th className="text-right px-5 py-2 font-medium" style={{ color: "var(--color-text-muted)" }}>次数</th>
          {isVideo && (
            <th className="text-right px-5 py-2 font-medium" style={{ color: "var(--color-text-muted)" }}>总时长</th>
          )}
          {showTokens && (
            <th className="text-right px-5 py-2 font-medium" style={{ color: "var(--color-text-muted)" }}>输入 / 输出 Token</th>
          )}
          <th className="text-right px-5 py-2 font-medium" style={{ color: "var(--color-text-muted)" }}>
            {isVideo ? "均价/秒" : `均价/${unitLabel}`}
          </th>
          <th className="text-right px-5 py-2 font-medium" style={{ color: "var(--color-text-muted)" }}>费用</th>
        </tr>
      </thead>
      <tbody>
        {details.map((d, i) => (
          <tr
            key={`${d.type || ""}|${d.provider || ""}|${d.model || ""}|${d.subModel || ""}`}
            className="transition-all duration-200"
            style={{ borderTop: i > 0 ? "1px solid var(--color-border-primary)" : "none" }}
          >
            <td className="px-5 py-3 font-medium" style={{ color: "var(--color-text-primary)" }}>
              {getModelName(d.model)}
              {d.provider && <span className="ml-2 text-xs opacity-50">{getPublicVideoLabel(d.provider, d.provider)}</span>}
              <div className="flex gap-1 mt-1">
                {d.estimatedCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ color: "var(--color-warning)", backgroundColor: "color-mix(in srgb, var(--color-warning) 9%, transparent)" }}>
                    {d.estimatedCount} 次估算
                  </span>
                )}
                {d.zeroCostCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ color: "var(--color-error)", backgroundColor: "color-mix(in srgb, var(--color-error) 9%, transparent)" }}>
                    {d.zeroCostCount} 次待补价
                  </span>
                )}
              </div>
            </td>
            <td className="text-right px-5 py-3" style={{ color: "var(--color-text-secondary)" }}>
              {d.count}
            </td>
            {isVideo && (
              <td className="text-right px-5 py-3" style={{ color: "var(--color-text-secondary)" }}>
                {formatDuration ? formatDuration(d.totalDuration || 0) : "-"}
              </td>
            )}
            {showTokens && (
              <td className="text-right px-5 py-3 tabular-nums" style={{ color: "var(--color-text-secondary)" }}>
                {formatTokens(d.promptTokens)} / {formatTokens(d.completionTokens)}
              </td>
            )}
            <td className="text-right px-5 py-3" style={{ color: "var(--color-text-secondary)" }}>
              {formatCost(d.avgPrice || 0)}
            </td>
            <td className="text-right px-5 py-3 font-bold" style={{ color: theme.colors.bluePrimary }}>
              {formatCost(d.cost)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  </div>
);

export default CostStatisticsPage;
